import React, { memo, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRightIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { DashboardRoutes } from "../../routes/dashboard";
import { TWclassNames } from "../utils/Div";
import RoleOnly from "../../routes/roleOnly";
import { useCanManageHoursMonthlyOverview } from "../../routes/roleOnly";
import { useCanApproveHours } from "../../routes/roleOnly";

const SidebarNav = memo(({ onClick, collapsed }) => {
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(null);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const canManageHoursMonthlyOverview = useCanManageHoursMonthlyOverview();
  const canApproveHours = useCanApproveHours();

  const routes = useMemo(
    () => DashboardRoutes.filter((item) => item.sidebar),
    []
  );

  const isActiveRoute = (path) => location.pathname === path;

  const handleToggle = (title, hasChildren) => {
    if (hasChildren) {
      setOpenMenu((prev) => (prev === title ? null : title));
    } else {
      onClick(); // collapse mobile menu
    }
  };

  const handleSubMenuToggle = (title) => {
    setOpenSubMenu((prev) => (prev === title ? null : title));
  };

  // Recursive component for rendering nested menu items
  const renderMenuItem = (item, level = 0, parentKey = "") => {
    const key = parentKey ? `${parentKey}-${item.title}` : item.title;
    const current = isActiveRoute(item.path);
    const hasChildren = item?.children?.some((child) => child.sidebar);
    const sidebarChildren = item?.children?.filter((child) => {
      if (!child.sidebar) return false;
      if (child.path === "/dashboard/hours/monthly-overview") {
        return canManageHoursMonthlyOverview;
      }
      if (child.path === "/dashboard/hours/approval") {
        return canApproveHours;
      }
      return true;
    }) || [];
    const isOpen = level === 0 ? openMenu === item.title : openSubMenu === key;

    if (level === 0) {
      // Top level menu items
      return (
        <RoleOnly key={key} role={item?.role} roles={item?.roles} feature={item?.feature} action={item?.action} requireAdmin={item?.requireAdmin} requireAllRoles={item?.requireAllRoles}>
          <li className="min-w-0">
            {!hasChildren ? (
              <Link
                to={item.path}
                onClick={() => handleToggle(item.title, hasChildren)}
                className={TWclassNames(
                  current
                    ? "bg-gray-50 text-blue-400"
                    : "text-gray-400 hover:text-blue-400 hover:bg-gray-50",
                  "group flex items-center gap-2 text-sm font-semibold cursor-pointer transition-all duration-300",
                  collapsed ? "rounded-md p-1.5" : "rounded-xl p-2"
                )}
              >
                <item.icon className="h-6 w-6 shrink-0" />
                {!collapsed && (
                  <span className="min-w-0 overflow-hidden transition-all duration-300 w-auto opacity-100">
                    {item.title}
                  </span>
                )}
                {item.count && !collapsed && (
                  <span className="ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-gray-900 px-2.5 py-0.5 text-center text-xs font-medium leading-5 text-gray-400 ring-1 ring-inset ring-gray-700">
                    {item.count}
                  </span>
                )}
              </Link>
            ) : (
              <>
                <div
                  onClick={() => handleToggle(item.title, hasChildren)}
                  className={TWclassNames(
                    current
                      ? "bg-gray-50 text-blue-400"
                      : "text-gray-400 hover:text-blue-400 hover:bg-gray-50",
                    "group flex items-center gap-2 text-sm font-semibold cursor-pointer transition-all duration-300",
                    collapsed ? "rounded-md p-1.5" : "rounded-xl p-2"
                  )}
                >
                  <item.icon className="h-6 w-6 shrink-0" />
                  {!collapsed && (
                    <span className="min-w-0 overflow-hidden transition-all duration-300 w-auto opacity-100">
                      {item.title}
                    </span>
                  )}
                  {item.count && !collapsed && (
                    <span className="ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-gray-900 px-2.5 py-0.5 text-center text-xs font-medium leading-5 text-gray-400 ring-1 ring-inset ring-gray-700">
                      {item.count}
                    </span>
                  )}
                </div>

                {hasChildren && !collapsed && isOpen && (
                  <ul className="pt-2 pl-8 flex flex-col gap-1">
                    {sidebarChildren.map((subItem) => (
                      <RoleOnly
                        key={`sub-${key}-${subItem.title}`}
                        role={subItem?.role}
                        roles={subItem?.roles}
                        feature={subItem?.feature}
                        action={subItem?.action}
                        requireAdmin={subItem?.requireAdmin}
                        requireAllRoles={subItem?.requireAllRoles}
                        drivingFleetVehiclesList={
                          subItem?.drivingFleetVehiclesList
                        }
                      >
                        <li>
                          {renderMenuItem(subItem, level + 1, key)}
                        </li>
                      </RoleOnly>
                    ))}
                  </ul>
                )}
              </>
            )}
          </li>
        </RoleOnly>
      );
    } else {
      // Nested menu items (level 1+)
      const subHasChildren = item?.children?.some((child) => child.sidebar);
      const subSidebarChildren = item?.children?.filter((child) => child.sidebar) || [];
      const subIsOpen = openSubMenu === key;
      const subCurrent = isActiveRoute(item.path);

      return (
        <>
          {!subHasChildren ? (
            <Link
              to={item.path}
              onClick={() => onClick()}
              className={TWclassNames(
                subCurrent
                  ? "bg-gray-50 text-blue-400"
                  : "text-gray-400 hover:text-blue-400 hover:bg-gray-50",
                "group flex items-center rounded-md py-2 pl-2 pr-2 text-sm transition-colors"
              )}
            >
              <ArrowRightIcon className="h-4 w-4 mr-2" />
              {item.title}
            </Link>
          ) : (
            <>
              <div
                onClick={() => handleSubMenuToggle(key)}
                className={TWclassNames(
                  subCurrent
                    ? "bg-gray-50 text-blue-400"
                    : "text-gray-400 hover:text-blue-400 hover:bg-gray-50",
                  "group flex items-center justify-between rounded-md py-2 pl-2 pr-2 text-sm cursor-pointer transition-colors"
                )}
              >
                <div className="flex items-center">
                  <ArrowRightIcon className="h-4 w-4 mr-2" />
                  {item.title}
                </div>
                <ChevronRightIcon
                  className={TWclassNames(
                    "h-4 w-4 transition-transform duration-200",
                    subIsOpen ? "rotate-90" : ""
                  )}
                />
              </div>
              {subHasChildren && subIsOpen && (
                <ul className="pt-1 pl-6 flex flex-col gap-1">
                  {subSidebarChildren.map((nestedItem) => (
                    <RoleOnly
                      key={`nested-${key}-${nestedItem.title}`}
                      role={nestedItem?.role}
                      roles={nestedItem?.roles}
                      feature={nestedItem?.feature}
                      action={nestedItem?.action}
                      requireAdmin={nestedItem?.requireAdmin}
                      requireAllRoles={nestedItem?.requireAllRoles}
                      drivingFleetVehiclesList={
                        nestedItem?.drivingFleetVehiclesList
                      }
                    >
                      <li>
                        <Link
                          to={nestedItem.path}
                          onClick={() => onClick()}
                          className={TWclassNames(
                            isActiveRoute(nestedItem.path)
                              ? "bg-gray-50 text-blue-400"
                              : "text-gray-400 hover:text-blue-400 hover:bg-gray-50",
                            "group flex items-center rounded-md py-1.5 pl-2 pr-2 text-xs transition-colors"
                          )}
                        >
                          <ArrowRightIcon className="h-3 w-3 mr-2" />
                          {nestedItem.title}
                        </Link>
                      </li>
                    </RoleOnly>
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      );
    }
  };

  return (
    <ul role="list" className="flex flex-1 flex-col gap-y-0 px-2">
      <li>
        <ul role="list" className="-mx-2 flex flex-col gap-0 list-none [&>li]:m-0 [&>li]:p-0">
          {routes.map((item, idx) => renderMenuItem(item, 0, `route-${idx}`))}
        </ul>
      </li>
    </ul>
  );
});

export default SidebarNav;

SidebarNav.displayName = "SidebarNav";
SidebarNav.whyDidYouRender = true;
